import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import {
  createGuestRequestApi,
  updateGuestRequestApi,
  patchGuestRequestApi,
  deleteGuestRequestApi,
  deleteGuestRequestsApi,
} from '../api/guestRequestApi'
import { type GuestRequest } from '../utils/types'
import LogTool from '../../../logger/logTools'
import { useSmartPatchMutation } from "../../../api/hooks/useSmartMutations";
import { guestRequestKeys } from "../utils/queryKeys";
import { getPKfromSelf } from "../../../utils/functions";

const log = new LogTool({ context: 'GuestRequestMutations', enable: true, logLevel: 'warn' })

/**
 * Hook to create a new guest request
 */
export function useCreateGuestRequest(): UseMutationResult<
  GuestRequest,
  Error,
  { requestData: any; files?: File[] }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ requestData, files }) => createGuestRequestApi(requestData, files),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: guestRequestKeys.lists() })
      log.info('Guest request created successfully')
    },
    onError: (error) => {
      log.error('Error creating guest request:', error)
    },
  })
}

/**
 * Hook to update a guest request
 */
export function useUpdateGuestRequest(): UseMutationResult<
  GuestRequest,
  Error,
  { idOrSelf: string | number; data: any }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ idOrSelf, data }) => updateGuestRequestApi(idOrSelf, data),
    onSuccess: async (updated) => {
      // Update the cache manually to preserve table order
      queryClient.setQueriesData(
        { queryKey: guestRequestKeys.lists() },
        (oldData: any) => {
          if (!oldData?.results) return oldData
          return {
            ...oldData,
            results: oldData.results.map((item: GuestRequest) =>
              item.key === updated.key ? updated : item
            ),
          }
        }
      )
      // Invalidate to reload with expanded data (editors, files, etc.)
      await queryClient.invalidateQueries({ 
        queryKey: guestRequestKeys.lists(),
        refetchType: 'active'
      })
      await queryClient.invalidateQueries({
        queryKey: guestRequestKeys.detail(Number(getPKfromSelf(updated.self))),
      })
      log.info('Guest request updated successfully')
    },
    onError: (error) => {
      log.error('Error updating guest request:', error)
    },
  })
}

/**
 * Hook to patch a guest request
 */
export function usePatchGuestRequest(): UseMutationResult<
  GuestRequest,
  Error,
  { idOrSelf: string | number; data: Partial<GuestRequest> }
> {
  const queryClient = useQueryClient()

  return useSmartPatchMutation({
    queryKeyFactory: guestRequestKeys,
    // mutationFn: () => patchGuestRequestApi()
    mutationFn: ({ idOrSelf, data }) => patchGuestRequestApi(idOrSelf, data),
    onSuccess: (updated) => {
      log.info('Success patching guest request: ', updated.key)
    },
    onError: (error) => {
      log.error('Error patching guest request:', error)
    },
  }, queryClient)
}

/**
 * Hook to delete multiple guest requests
 */
export function useDeleteGuestRequests(): UseMutationResult<void, Error, (string | number)[]> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteGuestRequestsApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: guestRequestKeys.lists() })
      log.info('Guest requests deleted successfully')
    },
    onError: (error) => {
      log.error('Error deleting guest requests:', error)
    },
  })
}